type Searchable = { preferred?: string | null; synonyms?: (string | null)[] | null };

/** A term, and the synonym the search matched when it did not match the ja-ord. */
export type Hit<T> = { term: T; synonymMatch?: string };

/**
 * The part of a ja-ord that appears in running text: cut at the first «,» or « (», so
 * «Frekvens, f (Hz)» is looked for as «Frekvens».
 */
export const matchableWord = (word: string) => word.split(/,| \(/)[0].trim();

/**
 * How well `word` matches `q` (both lowercase): 0 exact, 1 starts with it, 2 a later word starts
 * with it (e.g. «lydnivå» in «A-veid lydnivå»), 3 anywhere else, null no match.
 */
const rank = (word: string | null | undefined, q: string): number | null => {
  const w = word?.toLowerCase();
  if (!w?.includes(q)) return null;
  if (w === q || matchableWord(w) === q) return 0;
  if (w.startsWith(q)) return 1;
  if (new RegExp(`(?<![\\p{L}\\p{N}])${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'u').test(w)) return 2;
  return 3;
};

const best = (ranks: (number | null)[]) => Math.min(...ranks.map((r) => r ?? Infinity));

/**
 * Terms matching `query` (case-insensitive substring) in the ja-ord or a synonym, best
 * matches first: exact, then starts with, then word start, then anywhere; ties keep the input order.
 * An empty query returns every term. Used by the Ordliste page and the dictionary sidebar in the CMS.
 */
export function searchTerms<T extends Searchable>(terms: T[], query: string): Hit<T>[] {
  const q = query.trim().toLowerCase();
  if (!q) return terms.map((term) => ({ term }));

  return terms
    .map((t, index) => {
      const preferred = rank(t.preferred, q);
      const synonyms = (t.synonyms ?? []).map((s) => ({ word: s, rank: rank(s, q) }));
      const score = best([preferred, ...synonyms.map((s) => s.rank)]);
      if (score === Infinity) return null;

      // Show which synonym matched, unless the ja-ord matches at least as well.
      const s = synonyms.filter((x) => x.rank !== null).sort((a, b) => a.rank! - b.rank!)[0];
      const hit: Hit<T> = { term: t };
      if (s && (preferred === null || preferred > score)) hit.synonymMatch = s.word!;
      return { hit, score, index };
    })
    .filter((x): x is { hit: Hit<T>; score: number; index: number } => !!x)
    .sort((a, b) => a.score - b.score || a.index - b.index)
    .map((x) => x.hit);
}
